import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { isFramed } from "@/lib/framed";

function sendEvent(payload: Record<string, string>) {
  // Admin and editor previews run in frames and must not count as visits.
  if (isFramed()) return;
  supabase.functions.invoke("track-analytics", {
    body: payload,
  }).catch(() => {
    // Silently fail - analytics should never break the app
  });
}

/**
 * Extracts a human-readable label from a clicked element by walking up the DOM.
 */
function extractLabel(el: HTMLElement): string {
  // Check aria-label first
  const aria = el.getAttribute("aria-label");
  if (aria) return aria;

  // For links, use text content or href
  const text = el.innerText?.trim();
  if (text && text.length <= 80) return text;

  // For images, use alt
  if (el.tagName === "IMG") return (el as HTMLImageElement).alt || "image";

  // Fallback: walk up to find text
  const parent = el.closest("a, button, [role='button']");
  if (parent && parent !== el) {
    const parentAria = parent.getAttribute("aria-label");
    if (parentAria) return parentAria;
    const parentText = (parent as HTMLElement).innerText?.trim();
    if (parentText && parentText.length <= 80) return parentText;
  }

  return el.tagName.toLowerCase();
}

/**
 * Finds the closest interactive element (link or button) from a click target.
 */
function findInteractiveElement(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof HTMLElement)) return null;
  return target.closest("a, button, [role='button']");
}

/**
 * Automatically tracks page views on route changes and
 * all button/link clicks via a global event listener.
 * Returns a `trackClick` function for manual tracking if needed.
 */
export function useAnalytics() {
  const location = useLocation();
  const lastTrackedPath = useRef<string>("");

  // Track page views on route change
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/112076")) return;
    if (path === lastTrackedPath.current) return;
    lastTrackedPath.current = path;

    sendEvent({
      event_type: "page_view",
      page_path: path,
      referrer: document.referrer || "",
    });
  }, [location.pathname]);

  // Global click tracking for all buttons and links
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const el = findInteractiveElement(e.target);
      if (!el) return;

      const path = location.pathname;
      if (path.startsWith("/112076")) return;

      const label = extractLabel(el);
      const href = el.getAttribute("href") || "";
      const tagName = el.tagName.toLowerCase();

      sendEvent({
        event_type: "button_click",
        page_path: path,
        button_label: label,
        referrer: document.referrer || "",
      });
    };

    document.addEventListener("click", handler, { capture: true, passive: true });
    return () => document.removeEventListener("click", handler, true);
  }, [location.pathname]);

  const trackClick = useCallback((buttonLabel: string) => {
    const path = location.pathname;
    if (path.startsWith("/112076")) return;

    sendEvent({
      event_type: "button_click",
      page_path: path,
      button_label: buttonLabel,
      referrer: document.referrer || "",
    });
  }, [location.pathname]);

  return { trackClick };
}
